<script setup lang="ts">
import PreviewTabsCard from '@/components/generic/PreviewTabsCard.vue';
import MovementTabs from '@/components/movement-tabs/MovementTabs.vue';
import MusicXmlPreview from "@/components/MusicXmlPreview.vue";
import MidiPlayer from "@/components/MidiPlayer.vue";
import type {Grip} from "@/components/harmonika/gripTypes";
import type {NoteInfo} from "@/composables/musicXmlDom";
import {useMusicUploadStore} from "@/stores/useMusicUploadStore.ts";
import {useFileHistoryStore} from "@/stores/useFileHistoryStore";
import {Icon} from '@iconify/vue';
import {storeToRefs} from "pinia";
import {computed} from "vue";

const conversionStore = useMusicUploadStore();
const {
  isPending, isHavingFile, isHavingGriffschriftResult,
  selectedFileContent,
  previewFileContent,
  isUploading,
  currentPdfObjectUrl,
  analysisResult,
} = storeToRefs(conversionStore);

// Tonhöhen, die auf der Harmonika nicht existieren – die Eingabe-Vorschau färbt sie rot, damit
// man vor dem Konvertieren sieht, wo Lücken sind. Quelle ist die ohnehin laufende Analyse; die
// Konvertierung macht aus genau diesen Noten rote Pausen (dieselbe Entscheidung, siehe
// `griffschrift/selection/selector.py::is_pitch_playable`).
const unplayableNotes = computed<string[]>((oldValue?: string[]) => {
  const next = analysisResult.value?.unmappable_notes ?? [];
  if (oldValue && oldValue.length === next.length && oldValue.every((n, i) => n === next[i])) return oldValue;
  return next;
});

// Einziger "Zurücksetzen"-Button: lädt den vorherigen Stand der Bearbeitungskette (Undo).
// Mehrfaches Klicken läuft die Kette bis zum Original zurück; Zwischenstände bleiben im Verlauf.
const historyStore = useFileHistoryStore();
const {canLoadPrevious} = storeToRefs(historyStore);

function loadPreviousState() {
  void historyStore.loadPreviousState();
}

async function saveUpdate(updatedContent: { musicXml: string }) {
  await conversionStore.updateSelectedFileContent(updatedContent.musicXml);
  await conversionStore.analyzeSelected();
}

// Griff der ausgewählten Ausgabe-Note tauschen. Die Note reist im Event mit (wie bei
// `apply-bass-lyric`) – kein Zugriff auf die Kind-Instanz per Template-Ref nötig.
function onApplyGrip({note, grip}: { note: NoteInfo; grip: Grip }) {
  void conversionStore.applyGripToConvertedNote(note, grip);
}

// Bass-Notation der ausgewählten Ausgabe-Elemente setzen/löschen (client-seitig, nur conversionResult).
function onApplyBassLyric(payload: { notes: NoteInfo[]; text: string | null }) {
  conversionStore.applyBassLyricToConvertedNotes(payload.notes, payload.text);
}

// Gesangstext-Sitzung übernehmen. Die beiden Vorschauen schreiben in VERSCHIEDENE Ziele:
// Eingabe = neue Arbeitsbasis + Verlaufseintrag, Ausgabe = nur die Konvertierungs-Vorschau
// (wird bei der nächsten Konvertierung überschrieben, wie Griff-/Bass-Edits).
/**
 * Spielweise eines Takts im Bass-System – die Vorschau meldet nur, der Store baut neu auf.
 *
 * Bewusst hier und nicht in der Vorschau: die ruft grundsätzlich keine Endpunkte (siehe
 * `webapp/CLAUDE.md`, „Notenvorschau"). Geschrieben wird der Vermerk serverseitig.
 */
async function onSetBassStyle(payload: { measureIndex: number; style: string }) {
  await conversionStore.setBassMeasureStyle(payload.measureIndex, payload.style);
}

function onApplyLyricsInput(payload: { musicXml: string }) {
  conversionStore.applyLyricsToSelectedFileContent(payload.musicXml);
}

// ALLE Edits der AUSGABE-Vorschau (Gesangstext-Sitzung UND „Übernehmen" der Kurzbefehl-Edits –
// auf der Griffschrift sind die Buttons für Transponieren/Diatonisch/Akkord bewusst ausgeblendet,
// die KURZBEFEHLE ↑/↓, Shift+↑/↓, Alt+↑/↓ gelten dort aber weiter). Ziel wie bei
// Griff/Bass: nur `conversionResult.music_xml`, KEIN Upload und KEIN Verlaufseintrag (die
// Ausgabe ist ein Derivat und wird bei der nächsten Konvertierung überschrieben) – deshalb
// NICHT `saveUpdate` wie auf der Eingabe-Vorschau.
function saveConvertedUpdate(payload: { musicXml: string }) {
  conversionStore.applyEditedConvertedXml(payload.musicXml);
}

const transpositionInfo = computed(() => {
  if (!isHavingFile.value)
    return 'Keine Datei hochgeladen. Bitte lade eine Datei hoch, um die Vorschau zu aktivieren.'
  if (!isHavingGriffschriftResult.value)
    return 'Die Griffschrift-Vorschau ist nur verfügbar, wenn die Konvertierung erfolgreich war.'
  return ''
})
</script>

<template>
  <div class="bg-transparent flex flex-col flex-1 min-h-0">

    <PreviewTabsCard>
      <template #input>
        <div v-if="currentPdfObjectUrl && isUploading" class="flex flex-1 flex-col gap-3 min-h-0 h-full">
          <div class="flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-700">
            <Icon icon="fa6-solid:spinner" class="animate-spin shrink-0"/>
            <span>PDF wird mit Audiveris in MusicXML konvertiert… Dies kann 30–120 Sekunden dauern.</span>
          </div>
          <iframe
              :src="currentPdfObjectUrl"
              class="w-full flex-1 rounded-lg border border-gray-200 aspect-[1/1.414] min-h-[50dvh] max-h-[90dvh]"
              title="PDF-Vorschau"
          />
        </div>
        <!--
          `v-else-if` (NICHT `v-show`, kritisch – nicht rückbauen): unter `v-show` wird
          `MusicXmlPreview` schon OHNE Datei gemountet – also eine OSMD-Instanz in einem
          `display:none`-Container mit Breite 0 (genau der Zustand, aus dem OSMDs
          `Cannot read properties of undefined (reading '0')` entsteht), und die
          dokument-weiten `onKeyStroke`-Handler der `ScoreEditToolbar` (Entf/Pfeile/Strg+C/V/B)
          wären aktiv, obwohl gar nichts angezeigt wird. Die Invariante lautet: Edit-Kürzel sind
          nur aktiv, solange die Toolbar gemountet/sichtbar ist.
          Kein `&& !isUploading`: bei Nicht-PDF-Uploads ist `currentPdfObjectUrl` null, sonst wären
          während des Uploads BEIDE Zweige aus und der Eingabe-Tab bliebe leer, statt den
          bisherigen Stand weiter zu zeigen.
        -->
        <div v-else-if="selectedFileContent" class="flex h-full  flex-col gap-3">
          <!-- Movement-Auswahl bei mehrteiligen PDFs (rendert nur bei > 1 Teil). -->
          <MovementTabs/>
          <!-- Wiedergabe des aktuell angezeigten Input-MusicXML (immer verfügbar, stateless). -->
          <div v-if="selectedFileContent" class="flex items-center justify-between gap-2 rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2">
            <div class="flex items-center gap-2">
              <MidiPlayer :music-xml="selectedFileContent"/>
              <button
                  type="button"
                  class="flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                  :disabled="!canLoadPrevious || isPending"
                  title="Lädt den vorherigen Stand (Zwischenstände bleiben im Verlauf erhalten)"
                  @click="loadPreviousState"
              >
                <Icon icon="fa6-solid:rotate-left" class="text-xs"/>
                Zurücksetzen
              </button>
            </div>
            <span class="text-xs font-medium text-gray-500">Eingabedatei anhören</span>

          </div>
          <!-- show-chord-inference-status nur hier: auf der Griffschrift-Ausgabe (unten) wäre der
               Status der Akkorderkennung bedeutungslos.
               enable-playback ebenfalls nur hier (kritisch – nicht auf die Ausgabe ausweiten): die
               Griffschrift ist keine spielbare Partitur (Knopf-Notation, Balgrichtung, Bass-Lyrics,
               rote Pausen für unspielbare Töne) und klänge zwangsläufig falsch. Ohne das Flag
               entsteht dort kein PlaybackManager und es werden keine SoundFonts geladen. -->
          <MusicXmlPreview
              class="min-h-0 flex-1"
              :file="selectedFileContent ?? undefined"
              :show-chord-inference-status="true"
              :show-input-transform-panels="true"
              :enable-lyric-edit="true"
              :enable-playback="true"
              :unplayable-notes="unplayableNotes"
              @save-xml="saveUpdate"
              @apply-lyrics="onApplyLyricsInput"
              @set-bass-style="onSetBassStyle"
          />
        </div>
      </template>
      <template #converted>
        <div class="flex h-full min-h-0 flex-col gap-3">
          <MusicXmlPreview
              class="min-h-0 flex-1"
              :file="previewFileContent ?? undefined"
              :enable-grips="true"
              :enable-bass-edit="true"
              :enable-lyric-edit="true"
              :show-advanced-tools="false"
              @save-xml="saveConvertedUpdate"
              @apply-grip="onApplyGrip"
              @apply-bass-lyric="onApplyBassLyric"
              @apply-lyrics="saveConvertedUpdate"
          />
        </div>
      </template>
    </PreviewTabsCard>
  </div>
</template>
